// These should be removed once we transition to webpack as it is throwing warnings due to having no import/exports
/* eslint-disable react/jsx-no-undef */

import { lazy, MouseEvent, Suspense } from 'react';
import CommentSelectPane from 'annotator/comment-select-pane';
import CommentViewPane, { YambaCommentViewPane } from 'annotator/comment-view-pane';
import {
  CommentCategory,
  CommentHelpersSummary,
  CommentInputAnnotation
} from 'annotator/annotator-types';
import { BellmereQA } from './bellmere-qa/qa-types.d';

const CommentEditPane = lazy(() => import('annotator/comment-edit-pane'));

type CommentInputPaneProps = {
  annotation: CommentInputAnnotation,
  annotationText: string,
  bulkDeleteMode: boolean,

  commentCategory: CommentCategory,
  commentHelpersSummary: CommentHelpersSummary,
  confirmingDeletion: boolean,
  hasSources: boolean,
  isEditable: boolean,
  isEditing: boolean,
  isEditingDisabled: boolean,
  markedForDeletion: boolean,
  onAssignCommentVariants: (newCommentHelperId: number, callback: () => void) => void,
  onCancelEdit: () => void,
  onCancelRemovingAnnotation: () => void,
  onCitationDelete: (reason: string) => void,
  onEditComment: () => void,
  onMarkedForDeletionChange: () => void,
  onRemoveComment: (e?: MouseEvent, reason?: string) => void,
  onSaveComment: (comment: string, commentHelperId: number) => void,
  onStartRemovingAnnotation: (fromTimestamp: string) => void,
  savingSubmission: boolean,
  viewingAsBellmereQa: boolean,
  setBellmereQAResult: (result: BellmereQA.AnnotationResult) => void,
  bellmereQAEditable: boolean,
  result: BellmereQA.AnnotationResult
}

const CommentInputPane = ({
  annotation,
  annotationText,
  bulkDeleteMode,
  commentCategory,
  commentHelpersSummary,
  confirmingDeletion,
  hasSources,
  isEditable,
  isEditing,
  isEditingDisabled,
  markedForDeletion,
  onAssignCommentVariants,
  onCancelEdit,
  onCancelRemovingAnnotation,
  onCitationDelete,
  onEditComment,
  onMarkedForDeletionChange,
  onRemoveComment,
  onSaveComment,
  onStartRemovingAnnotation,
  savingSubmission,
  viewingAsBellmereQa,
  setBellmereQAResult,
  bellmereQAEditable,
  result
}: CommentInputPaneProps) => {
  if (isEditing && isEditable) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <CommentEditPane
          comment={annotation.comment}
          commentHelperId={annotation.commentHelperId || annotation.commentHelperIds[0]}
          commentHelpersSummary={commentHelpersSummary}
          savingSubmission={savingSubmission}
          onSaveComment={onSaveComment}
          onCancelEdit={onCancelEdit}
          onAssignCommentVariants={onAssignCommentVariants}
          hasSources={hasSources}
        />
      </Suspense>
    );
  }

  if (bulkDeleteMode) {
    return (
      <CommentSelectPane
        selected={markedForDeletion}
        onChange={onMarkedForDeletionChange}
        disabled={confirmingDeletion || !hasSources}
      />
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ViewPane = typeof (window as any).reduxStore === 'undefined' ? CommentViewPane : YambaCommentViewPane;

  return (
    <ViewPane
      annotation={annotation}
      comment={annotation.comment}
      isCommentFlagged={annotation.isCommentFlagged}
      isEditable={isEditable}
      isEditingDisabled={isEditingDisabled || savingSubmission}
      savingSubmission={savingSubmission}
      onEditComment={onEditComment}
      onStartRemovingAnnotation={onStartRemovingAnnotation}
      onCancelRemovingAnnotation={onCancelRemovingAnnotation}
      onRemoveComment={onRemoveComment}
      commentCategory={commentCategory}
      hasSources={hasSources}
      annotationText={annotationText}
      timestamp={annotation.timestamp}
      onCitationDelete={onCitationDelete}
      viewingAsBellmereQa={viewingAsBellmereQa}
      setBellmereQAResult={setBellmereQAResult}
      bellmereQAEditable={bellmereQAEditable}
      result={result}
    />
  );
};

export default CommentInputPane;
